import React from 'react';
import { CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AeCampaignFunnelRow } from '@/types/assessmentEngine';

interface CampaignInviteeStatusTableProps {
  rows: AeCampaignFunnelRow[];
  canManage?: boolean;
  removingInviteId?: string | null;
  enablingInviteId?: string | null;
  onRemoveInvite?: (inviteId: string) => void;
  onEnableInvite?: (inviteId: string) => void;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '—';
  }
}

/** Per-invitee distribution journey (sent, clicked, started, complete). */
export const CampaignInviteeStatusTable: React.FC<CampaignInviteeStatusTableProps> = ({
  rows,
  canManage = false,
  removingInviteId = null,
  enablingInviteId = null,
  onRemoveInvite,
  onEnableInvite,
}) => {
  const { t } = useLanguage();

  if (rows.length === 0) {
    return <p className="text-sm text-[var(--color-secondary-text)]">{t('assessmentEngine.hub.noInvites')}</p>;
  }

  const showActions = canManage && (onRemoveInvite || onEnableInvite);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[var(--color-secondary-text)] border-b border-[var(--color-border)]">
            <th className="py-2 pr-2">{t('assessmentEngine.hub.email')}</th>
            <th className="py-2 pr-2">{t('assessmentEngine.hub.funnelSent')}</th>
            <th className="py-2 pr-2">{t('assessmentEngine.hub.funnelClicked')}</th>
            <th className="py-2 pr-2">{t('assessmentEngine.hub.funnelStarted')}</th>
            <th className="py-2 pr-2">{t('assessmentEngine.hub.funnelComplete')}</th>
            {showActions && (
              <th className="py-2 w-20 text-right" scope="col">
                <span className="sr-only">{t('assessmentEngine.hub.inviteeActions')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const optedOut = Boolean(row.opted_out_at);
            const disabled = Boolean(row.disabled_at) || optedOut;
            return (
              <tr
                key={row.invite_id || row.email}
                className={`border-b border-[var(--color-border)] ${disabled ? 'opacity-70' : ''}`}
              >
                <td className="py-2 pr-2 text-[var(--color-surface-text)]">
                  <span>{row.email}</span>
                  {optedOut ? (
                    <span className="ml-2 text-xs text-[var(--color-secondary-text)]">
                      ({t('assessmentEngine.hub.inviteeOptedOut')})
                    </span>
                  ) : disabled ? (
                    <span className="ml-2 text-xs text-[var(--color-secondary-text)]">
                      ({t('assessmentEngine.hub.inviteeOutOfPool')})
                    </span>
                  ) : null}
                </td>
                <td className="py-2 pr-2">{fmtDate(row.sent_at)}</td>
                <td className="py-2 pr-2">{fmtDate(row.clicked_at)}</td>
                <td className="py-2 pr-2">{row.response_status ? fmtDate(row.started_at) : '—'}</td>
                <td className="py-2 pr-2">
                  {row.response_status === 'complete' ? fmtDate(row.completed_at) : row.response_status ?? '—'}
                </td>
                {showActions && (
                  <td className="py-2 text-right">
                    {row.invite_id && disabled && onEnableInvite ? (
                      <button
                        type="button"
                        disabled={enablingInviteId === row.invite_id}
                        className="rounded p-1 text-[var(--color-primary)] hover:bg-[var(--color-hover-bg)] disabled:opacity-50"
                        aria-label={
                          optedOut
                            ? t('assessmentEngine.hub.clearOptOutAndEnable')
                            : t('assessmentEngine.hub.enableParticipant')
                        }
                        title={
                          optedOut
                            ? t('assessmentEngine.hub.clearOptOutAndEnable')
                            : t('assessmentEngine.hub.enableParticipant')
                        }
                        onClick={() => onEnableInvite(row.invite_id)}
                      >
                        <CheckCircleIcon className="h-4 w-4" aria-hidden />
                      </button>
                    ) : null}
                    {row.invite_id && !disabled && onRemoveInvite ? (
                      <button
                        type="button"
                        disabled={removingInviteId === row.invite_id}
                        className="rounded p-1 text-[var(--color-error)] hover:bg-[var(--color-hover-bg)] disabled:opacity-50"
                        aria-label={t('assessmentEngine.hub.removeParticipant')}
                        title={t('assessmentEngine.hub.removeParticipant')}
                        onClick={() => onRemoveInvite(row.invite_id)}
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden />
                      </button>
                    ) : null}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
